import { queryParamsEventEmitter } from 'react-use-query-param-string';
import { restoreCarListScroll, saveCarListScroll, readCarListScroll, isCarListPath, initializeCarListHistory } from './src/helpers/carListHistory';
import type { GatsbyBrowser } from 'gatsby';
import { captureAdClickIds, clearAdClickIds } from './src/helpers/adTracking';
import {
  MARKETING_CONSENT_GRANTED_EVENT,
  MARKETING_CONSENT_REVOKED_EVENT,
} from './src/components/metaPixel';
import { CONSENT_MANAGER_READY_EVENT } from './src/components/cookieConsent';

let consentManagerReady = false;
let lastSearch = '';

function tryCapture() {
  if (consentManagerReady) {
    captureAdClickIds(lastSearch);
  }
}

export const onClientEntry: GatsbyBrowser['onClientEntry'] = async () => {
  // A client-only form can arrive inside Azure's populated 404 fallback HTML.
  // Give Gatsby an empty mount point so it renders the form without trying to
  // hydrate the unrelated error-page markup. Keep the nine dynamic routes.
  if ((window as Window & { pagePath?: string }).pagePath === '/404.html' &&
      /^(?:\/(?:en|fr))?\/cars\/[^/]+\/(?:request|financing-request|trade-in)\/?$/.test(window.location.pathname)) {
    if (document.readyState === 'loading') {
      await new Promise<void>(resolve => document.addEventListener('DOMContentLoaded', () => resolve(), { once: true }));
    }
    document.getElementById('gatsby-focus-wrapper')?.replaceChildren();
  }
  initializeCarListHistory(window);
  lastSearch = window.location.search;
  window.addEventListener('popstate', () => queryParamsEventEmitter.emit('update'));
  window.addEventListener('pagehide', () => saveCarListScroll(window, {
    pathname: window.location.pathname, key: window.history.state?.key, state: window.history.state
  }));

  window.addEventListener(CONSENT_MANAGER_READY_EVENT, () => {
    consentManagerReady = true;
    tryCapture();
  });
  window.addEventListener(MARKETING_CONSENT_GRANTED_EVENT, () => {
    consentManagerReady = true;
    tryCapture();
  });
  window.addEventListener(MARKETING_CONSENT_REVOKED_EVENT, () => {
    clearAdClickIds();
  });
};

export const onRouteUpdate: GatsbyBrowser['onRouteUpdate'] = ({ location }) => {
  queryParamsEventEmitter.emit('update');
  lastSearch = location.search;
  tryCapture();
};

export const onPreRouteUpdate: GatsbyBrowser['onPreRouteUpdate'] = ({ prevLocation }) => {
  if (prevLocation) saveCarListScroll(window, prevLocation);
};

export const shouldUpdateScroll: GatsbyBrowser['shouldUpdateScroll'] = ({
  routerProps: { location }, prevRouterProps, getSavedScrollPosition
}) => {
  if (isCarListPath(location.pathname) && !location.hash) {
    if (prevRouterProps?.location.pathname === location.pathname) return false;
    const saved = readCarListScroll(window, location) || getSavedScrollPosition(location);
    restoreCarListScroll(window, [saved?.[0] || 0, saved?.[1] || 0]);
    return false;
  }
  return true;
};
